// Client mirror of stageSpec validation. The server remains authoritative.
import { toFa } from '../utils/faFormat';

const MODES = ['ai_only', 'ai_then_supervisor'];
const STATUSES = ['not_ok', 'needs_confirmation', 'not_verifiable'];
const SEVERITIES = ['critical', 'major', 'minor', 'documentation'];
export const DEFAULT_PASS_RULE = Object.freeze({
  block_statuses: Object.freeze(['not_ok']),
  block_severities: Object.freeze(['critical', 'major']),
  min_confidence: null,
  block_insufficient_evidence: true,
});
export const CHALLENGE_REQUIRED_MESSAGE = 'عکسی که کد دست‌نویس دارد باید اجباری باشد (حداقل یک عکس).';
export const AI_ONLY_NOTE_MESSAGE = 'در حالت «فقط هوش مصنوعی» یادداشت را کسی نمی‌خواند؛ یادداشت اجباری را بردارید یا این مرحله را «هوش مصنوعی + ناظر» کنید.';
const record = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const integer = (v, min, max) => Number.isInteger(v) && v >= min && v <= max;
const number = (v, min, max) => Number.isFinite(v) && v >= min && v <= max;
const text = (v, min, max) => typeof v === 'string' && [...v.trim()].length >= min && [...v].length <= max;
const result = (errors) => ({ ok: errors.length === 0, errors });
function unknownKeys(value, keys, field, errors) {
  for (const key of Object.keys(value)) if (!keys.includes(key)) {
    errors.push({ field: `${field ? `${field}.` : ''}${key}`, message: 'فیلد ناشناخته مجاز نیست.' });
  }
}

// Range references stay independent of the carrier settings store.
export const RANGE_DEFAULTS = Object.freeze({ optical_power_dbm: { min: -27, max: -8 },
  'optical_power_dbm.warn': { warn_min: -27, warn_max: -8 },
  'trench_min_depth_cm.sidewalk': { min: 80 }, 'trench_min_depth_cm.roadway': { min: 120 },
  'speed_test.min_ratio': { warn_min: 0.70 } });
export const DOCUMENT_KINDS = Object.freeze({ rf_plan: 'نقشهٔ مسیر', approval_email: 'ایمیل تأیید', permit: 'مجوز حفاری', otdr: 'آزمایش OTDR', handover_visit: 'عکس بازدید تحویل', other: 'سایر' });
export const FIELD_TYPES = Object.freeze({ number: 'عدد', text: 'متن', choice: 'گزینه', identifier: 'شناسه' });
export const normalizeFieldDigits = (v) => String(v).replace(/[۰-۹٠-٩]/g, (d) => String(d.charCodeAt(0) - (d <= '٩' ? 1632 : 1776))).replace(/٫/g, '.').replace(/−/g, '-');
const RANGE_KEYS = ['min', 'max', 'warn_min', 'warn_max', 'block_min', 'block_max'];
function validateFieldSpecs(spec, errors, policySnapshot) {
  const add = (field, message) => errors.push({ field, message });
  if (spec.fields !== undefined && (!Array.isArray(spec.fields) || spec.fields.length > 12)) add('evidence.fields', 'حداکثر ۱۲ فیلد مجاز است.');
  const keys = new Set();
  if (Array.isArray(spec.fields)) spec.fields.forEach((f, i) => {
    const path = `evidence.fields.${i}`;
    if (!record(f)) { add(path, 'فیلد معتبر نیست.'); return; }
    unknownKeys(f, ['key', 'label_fa', 'type', 'unit', 'required', 'choices', ...RANGE_KEYS, 'settings_ref'], path, errors);
    if (typeof f.key !== 'string' || !/^[a-z][a-z0-9_]{0,31}$/.test(f.key) || keys.has(f.key)) add(`${path}.key`, 'شناسهٔ فیلد معتبر و بدون تکرار باشد.');
    keys.add(f.key);
    if (!text(f.label_fa, 2, 80)) add(`${path}.label_fa`, 'عنوان فیلد بین ۲ و ۸۰ نویسه باشد.');
    if (!Object.hasOwn(FIELD_TYPES, f.type)) add(`${path}.type`, 'نوع فیلد معتبر نیست.');
    if (typeof f.required !== 'boolean') add(`${path}.required`, 'اجباری بودن فیلد را مشخص کنید.');
    if (f.unit !== undefined && !text(f.unit, 1, 24)) add(`${path}.unit`, 'واحد حداکثر ۲۴ نویسه باشد.');
    if (f.type === 'choice') {
      if (!Array.isArray(f.choices) || !integer(f.choices.length, 1, 30) || f.choices.some((c) => !text(c, 1, 80) || normalizeFieldDigits(c).trim() !== c)
        || new Set(f.choices).size !== f.choices.length) add(`${path}.choices`, 'گزینه‌ها معتبر، بدون تکرار و با ارقام لاتین باشند.');
    } else if (f.choices !== undefined) add(`${path}.choices`, 'گزینه فقط برای فیلد گزینه‌ای مجاز است.');
    for (const k of RANGE_KEYS) if (f[k] !== undefined && (f.type !== 'number' || !Number.isFinite(f[k]))) add(`${path}.${k}`, 'حد عددی معتبر نیست.');
    if (f.settings_ref !== undefined && (f.type !== 'number' || !Object.hasOwn(RANGE_DEFAULTS, f.settings_ref))) add(`${path}.settings_ref`, 'مرجع تنظیمات معتبر نیست.');
    for (const prefix of ['', 'warn_', 'block_']) if (f[`${prefix}min`] !== undefined && f[`${prefix}max`] !== undefined && f[`${prefix}min`] > f[`${prefix}max`]) add(path, 'حداقل نباید بیشتر از حداکثر باشد.');
    const range = rangeCandidate(f, policySnapshot);
    if (!rangeConsistent(range)) add(path, 'بازهٔ نهایی فیلد با تنظیمات اپراتور سازگار نیست؛ حدود هشدار و مسدودکننده را اصلاح کنید.');
  });
  if (spec.requires_documents !== undefined && (!Array.isArray(spec.requires_documents) || spec.requires_documents.some((k) => k === 'handover_visit' || !Object.hasOwn(DOCUMENT_KINDS, k))
      || new Set(spec.requires_documents).size !== spec.requires_documents.length)) add('evidence.requires_documents', 'مدارک پیش‌نیاز معتبر و بدون تکرار باشند.');
}
function rangeCandidate(spec, policySnapshot = {}) {
  const defaults = Object.hasOwn(RANGE_DEFAULTS, spec.settings_ref) ? RANGE_DEFAULTS[spec.settings_ref] : {};
  // The explicit warning alias preserves existing hard optical references and pinned jobs.
  const policyRef = spec.settings_ref === 'optical_power_dbm.warn' ? 'optical_power_dbm' : spec.settings_ref;
  const setting = (typeof policyRef === 'string' ? policyRef.split('.').reduce((v, key) => record(v) && Object.hasOwn(v, key) ? v[key] : undefined, policySnapshot) : undefined);
  const referenced = spec.settings_ref === 'optical_power_dbm.warn' ? {
    warn_min: record(setting) && Object.hasOwn(setting, 'min') && Number.isFinite(setting.min) ? setting.min : defaults.warn_min,
    warn_max: record(setting) && Object.hasOwn(setting, 'max') && Number.isFinite(setting.max) ? setting.max : defaults.warn_max,
  } : record(setting) ? { ...defaults, ...setting } : Number.isFinite(setting)
    ? { [spec.settings_ref === 'speed_test.min_ratio' ? 'warn_min' : 'min']: setting } : defaults;
  const range = { ...Object.fromEntries(RANGE_KEYS.filter((k) => spec[k] !== undefined).map((k) => [k, spec[k]])), ...referenced };
  if (spec.settings_ref && referenced.min !== undefined) range.block_min = referenced.min;
  if (spec.settings_ref && referenced.max !== undefined) range.block_max = referenced.max;
  return Object.fromEntries([['block_min', range.block_min ?? range.min], ['block_max', range.block_max ?? range.max],
    ['warn_min', range.warn_min], ['warn_max', range.warn_max]].filter(([, v]) => v !== undefined));
}
function rangeConsistent(range) {
  const { block_min: min, block_max: max, warn_min: low, warn_max: high } = range;
  return !(min > max || low > high || low < min || low > max || high < min || high > max);
}
export function validateFieldRanges(spec, policySnapshot = {}) {
  const errors = [];
  (spec.fields || []).forEach((f, i) => {
    if (f.type === 'number' && !rangeConsistent(rangeCandidate(f, policySnapshot))) errors.push({ field: `evidence.fields.${i}`, key: f.key,
      message: 'بازهٔ نهایی فیلد با تنظیمات اپراتور سازگار نیست؛ حدود هشدار و مسدودکننده را اصلاح کنید.' });
  });
  return result(errors);
}
// Only legacy pinned conflicts use the fallback. Configuration always checks the raw candidate.
// Referenced hard bounds win. Widen the conflicting local opposite bound to that endpoint,
// then clamp warnings inside the hard interval. No valid range or stored policy is changed.
export function resolveRange(spec, policySnapshot = {}) {
  const range = rangeCandidate(spec, policySnapshot);
  if (range.block_min > range.block_max) {
    if (spec.settings_ref === 'optical_power_dbm') range.block_min = range.block_max;
    else range.block_max = range.block_min;
  }
  for (const key of ['warn_min', 'warn_max']) if (range[key] !== undefined) {
    if (range.block_min !== undefined) range[key] = Math.max(range[key], range.block_min);
    if (range.block_max !== undefined) range[key] = Math.min(range[key], range.block_max);
  }
  if (range.warn_min > range.warn_max) range.warn_max = range.warn_min;
  return range;
}

export function validateFieldValues(spec, values = {}) {
  const errors = []; const field_values = {};
  const add = (key, message) => errors.push({ field: `field_values.${key}`, message, reason: 'bad_field_value' });
  if (!record(values)) return { ...result([{ field: 'field_values', message: 'مقادیر فیلدها معتبر نیست.', reason: 'bad_field_values' }]), field_values };
  const fields = spec.fields || [];
  for (const key of Object.keys(values)) if (!fields.some((f) => f.key === key)) add(key, 'فیلد ناشناخته مجاز نیست.');
  fields.forEach((f) => {
    let value = Object.hasOwn(values, f.key) ? values[f.key] : undefined;
    if (value === undefined || value === null || value === '') { if (f.required) add(f.key, `«${f.label_fa}» اجباری است.`); return; }
    if (typeof value === 'string') value = normalizeFieldDigits(value).trim();
    if (f.type === 'number') {
      if (typeof value === 'string' && /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(value)) value = Number(value);
      if (!Number.isFinite(value)) { add(f.key, `«${f.label_fa}» باید عدد باشد.`); return; }
    } else if (typeof value !== 'string' || !text(value, 1, f.type === 'choice' ? 80 : 200) || (f.type === 'choice' && !f.choices.includes(value))) {
      add(f.key, `مقدار «${f.label_fa}» معتبر نیست.`); return;
    }
    Object.defineProperty(field_values, f.key, { value, enumerable: true, writable: true, configurable: true });
  });
  return { ...result(errors), field_values };
}
export function fieldFindings(spec, values = {}, policySnapshot = {}) {
  return (spec.fields || []).flatMap((f) => {
    const value = Object.hasOwn(values, f.key) ? values[f.key] : undefined;
    if (f.type !== 'number' || !Number.isFinite(value)) return [];
    const range = resolveRange(f, policySnapshot);
    const outside = (prefix) => (range[`${prefix}_min`] !== undefined && value < range[`${prefix}_min`]) || (range[`${prefix}_max`] !== undefined && value > range[`${prefix}_max`]);
    const severity = outside('block') ? 'major' : outside('warn') ? 'minor' : null;
    return severity ? [{ id: `field_range:${f.key}:${severity}`, hard: severity === 'major', rule: 'FIELD_RANGE', severity, field: f.key, value, range, status: 'not_ok', confidence: 1,
      description: `مقدار «${f.label_fa}» خارج از بازه است: ${value}${f.unit ? ` ${f.unit}` : ''}`,
      corrective_action: 'مقدار و کار اجراشده را بررسی و اصلاح کنید.' }] : [];
  });
}

// W2b must use this before offering a concession. Input is stage.evidence, not a model verdict.
export function isConcessionAllowed(stageSpec = {}) {
  return stageSpec.mandatory !== true && !(stageSpec.requires_documents || []).length;
}
export function checkFieldAcknowledgements(findings, acknowledgements = []) {
  const ids = findings.filter((f) => !f.hard).map((f) => f.id);
  return Array.isArray(acknowledgements) && new Set(acknowledgements).size === acknowledgements.length
    && acknowledgements.every((id) => ids.includes(id)) && ids.every((id) => acknowledgements.includes(id));
}

// Validators never mutate caller data. All return { ok, errors: [{ field, message }] }.
export function validatePassRule(rule) {
  const errors = [];
  if (!record(rule)) return result([{ field: 'pass_rule', message: 'قاعدهٔ قبولی معتبر نیست.' }]);
  unknownKeys(rule, ['block_statuses', 'block_severities', 'min_confidence', 'block_insufficient_evidence'], '', errors);
  for (const [field, allowed, message] of [
    ['block_statuses', STATUSES, 'دست‌کم یک نوع یافتهٔ بازدارنده انتخاب کنید.'],
    ['block_severities', SEVERITIES, 'دست‌کم یک شدت انتخاب کنید.'],
  ]) {
    const values = rule[field];
    if (!Array.isArray(values) || !values.length) errors.push({ field, message });
    else if (new Set(values).size !== values.length || values.some((v) => !allowed.includes(v))) {
      errors.push({ field, message: 'گزینه‌های قاعدهٔ قبولی معتبر و بدون تکرار باشند.' });
    }
  }
  if (rule.min_confidence !== null && (!number(rule.min_confidence, 0, 1)
      || Math.abs(rule.min_confidence * 20 - Math.round(rule.min_confidence * 20)) > 1e-9)) {
    errors.push({ field: 'min_confidence', message: 'حداقل اطمینان باید بین ۰ و ۱ باشد.' });
  }
  if (typeof rule.block_insufficient_evidence !== 'boolean') {
    errors.push({ field: 'block_insufficient_evidence', message: 'گزینهٔ کافی بودن عکس‌ها معتبر نیست.' });
  }
  return result(errors);
}

export function effectiveReview(stage, version = {}) {
  return {
    mode: stage.category_id == null ? 'ai_then_supervisor' : (stage.review_mode ?? version.review_mode ?? 'ai_then_supervisor'),
    rule: stage.pass_rule ?? version.pass_rule ?? DEFAULT_PASS_RULE,
  };
}

export function applyPassRule(verdict, rule) {
  const validation = validatePassRule(rule);
  if (!validation.ok) throw new Error(validation.errors[0].message);
  // Invalid or missing findings must not accidentally produce an automatic pass.
  if (!record(verdict) || !Array.isArray(verdict.violations)
      || !['compliant', 'non_compliant', 'partially_compliant', 'insufficient_evidence'].includes(verdict.overall_status)
      || !['proceed', 'correct_before_proceed', 'needs_more_photos'].includes(verdict.final_action)
      || verdict.violations.some((v) => !record(v) || !STATUSES.includes(v.status)
        || !SEVERITIES.includes(v.severity) || !number(v.confidence, 0, 1))) {
    throw new Error('نتیجهٔ هوش مصنوعی معتبر نیست.');
  }
  const blocking_ords = [];
  verdict.violations.forEach((v, i) => {
    if (v.rule === 'FIELD_RANGE' ? v.severity === 'major' : rule.block_statuses.includes(v.status) && rule.block_severities.includes(v.severity)
        && (rule.min_confidence === null || v.confidence >= rule.min_confidence)) blocking_ords.push(i);
  });
  const insufficient = verdict.overall_status === 'insufficient_evidence' || verdict.final_action === 'needs_more_photos';
  return { gate: blocking_ords.length || (insufficient && rule.block_insufficient_evidence) ? 'fail' : 'pass', blocking_ords, insufficient };
}

export function validateStageDefinition(stage, version = {}, policySnapshot = {}) {
  const errors = [];
  const add = (field, message) => errors.push({ field, message });
  if (!record(stage)) return result([{ field: 'stage', message: 'مرحله معتبر نیست.' }]);
  unknownKeys(stage, ['key', 'label_fa', 'instructions_fa', 'evidence', 'category_id', 'ai_focus_fa',
    'gate_mode', 'ai_attempt_cap', 'ai_deadline_s', 'review_mode', 'pass_rule', 'warn_minor'], '', errors);
  if (stage.warn_minor !== undefined && typeof stage.warn_minor !== 'boolean') add('warn_minor', 'گزینهٔ اخطار معتبر نیست.');
  if (typeof stage.key !== 'string' || !/^[a-z][a-z0-9_-]{1,40}$/.test(stage.key)) add('key', 'شناسهٔ مرحله معتبر نیست.');
  if (!text(stage.label_fa, 2, 80)) add('label_fa', 'عنوان لازم است.');
  if (stage.instructions_fa != null && !text(stage.instructions_fa, 0, 600)) add('instructions_fa', 'راهنمای نیروی اجرایی حداکثر ۶۰۰ نویسه باشد.');
  if (stage.category_id != null && (typeof stage.category_id !== 'string' || !/^[a-z0-9][a-z0-9_-]{0,63}$/.test(stage.category_id))) add('category_id', 'دستهٔ قواعد معتبر نیست.');
  if (stage.ai_focus_fa != null && !text(stage.ai_focus_fa, 0, 2000)) add('ai_focus_fa', 'تمرکز بررسی حداکثر ۲۰۰۰ نویسه باشد.');
  if (stage.gate_mode !== undefined && !['strict', 'on_submit'].includes(stage.gate_mode)) add('gate_mode', 'شیوهٔ باز شدن مرحله معتبر نیست.');
  if (stage.ai_attempt_cap !== undefined && !integer(stage.ai_attempt_cap, 1, 5)) add('ai_attempt_cap', 'تعداد بررسی بین ۱ و ۵ باشد.');
  if (stage.ai_deadline_s !== undefined && !integer(stage.ai_deadline_s, 300, 2100)) add('ai_deadline_s', 'مهلت پاسخ بین ۵ و ۳۵ دقیقه باشد.');
  if (stage.review_mode != null && !MODES.includes(stage.review_mode)) add('review_mode', 'نحوهٔ بازبینی معتبر نیست.');
  if (stage.pass_rule != null) errors.push(...validatePassRule(stage.pass_rule).errors.map((e) => ({ ...e, field: `pass_rule.${e.field}` })));
  const spec = stage.evidence;
  if (!record(spec)) { add('evidence', 'مدارک لازم معتبر نیست.'); return result(errors); }
  unknownKeys(spec, ['photos', 'note', 'note_min_chars', 'capture', 'fields', 'requires_documents', 'mandatory', 'warn_minor'], 'evidence', errors);
  validateFieldSpecs(spec, errors, policySnapshot);
  if (spec.warn_minor !== undefined && typeof spec.warn_minor !== 'boolean') add('evidence.warn_minor', 'گزینهٔ اخطار معتبر نیست.');
  if (spec.mandatory !== undefined && typeof spec.mandatory !== 'boolean') add('evidence.mandatory', 'اجباری بودن مرحله معتبر نیست.');
  if (!['none', 'optional', 'required'].includes(spec.note)) add('evidence.note', 'گزینهٔ یادداشت معتبر نیست.');
  if (!integer(spec.note_min_chars, 0, 500)) add('evidence.note_min_chars', 'حداقل یادداشت بین ۰ و ۵۰۰ نویسه باشد.');
  if (spec.note === 'required' && effectiveReview(stage, version).mode === 'ai_only') add('evidence.note', AI_ONLY_NOTE_MESSAGE);
  if (!Array.isArray(spec.photos) || !integer(spec.photos.length, 1, 8)) add('evidence.photos', 'بین ۱ و ۸ نوع عکس لازم است.');
  if (Array.isArray(spec.photos)) {
    const kinds = new Set(); let max = 0; let required = false;
    spec.photos.forEach((photo, i) => {
      const field = `evidence.photos.${i}`;
      if (!record(photo)) { add(field, 'نوع عکس معتبر نیست.'); return; }
      unknownKeys(photo, ['kind', 'label_fa', 'min', 'max', 'hint_fa'], field, errors);
      if (typeof photo.kind !== 'string' || !/^[a-z][a-z0-9_]{0,31}$/.test(photo.kind)) add(`${field}.kind`, 'شناسهٔ عکس معتبر نیست.');
      if (kinds.has(photo.kind)) add(`${field}.kind`, 'نوع عکس تکراری است.');
      kinds.add(photo.kind);
      if (!text(photo.label_fa, 2, 60)) add(`${field}.label_fa`, 'عنوان عکس بین ۲ و ۶۰ نویسه باشد.');
      if (!integer(photo.min, 0, 8) || !integer(photo.max, 1, 8) || photo.min > photo.max) add(field, 'حداقل و حداکثر عکس معتبر نیست.');
      if (integer(photo.max, 1, 8)) max += photo.max;
      if (integer(photo.min, 1, 8)) required = true;
      if (photo.hint_fa != null && !text(photo.hint_fa, 0, 300)) add(`${field}.hint_fa`, 'راهنمای عکس حداکثر ۳۰۰ نویسه باشد.');
    });
    if (max > 8) add('evidence.photos', 'جمع حداکثر عکس‌ها از ۸ بیشتر است.');
    if (!required && spec.note !== 'required') add('evidence', 'دست‌کم یک عکس اجباری یا یادداشت اجباری لازم است.');
  }
  const capture = spec.capture;
  if (!record(capture)) add('evidence.capture', 'شیوهٔ ثبت مدارک معتبر نیست.');
  else {
    unknownKeys(capture, ['camera_only', 'gps_required', 'gps_max_accuracy_m', 'max_distance_m', 'max_spread_m', 'time_check',
      'offline_capture', 'challenge_slot', 'audit_pct'], 'evidence.capture', errors);
    // Kept in the spec so the 0185 seed stays valid; neither can be switched off.
    if (capture.camera_only !== true) add('evidence.capture.camera_only', 'عکس فقط با دوربین برنامه گرفته می‌شود؛ این گزینه خاموش نمی‌شود.');
    if (capture.time_check !== true) add('evidence.capture.time_check', 'زمان عکس را سرور ثبت می‌کند؛ این گزینه خاموش نمی‌شود.');
    if (typeof capture.gps_required !== 'boolean') add('evidence.capture.gps_required', 'گزینهٔ ثبت مدارک معتبر نیست.');
    for (const [field, min, max, nullable] of [['gps_max_accuracy_m', 10, 500, false], ['max_distance_m', 25, 5000, true], ['max_spread_m', 5, 5000, true]]) {
      if (!(nullable && capture[field] === null) && !number(capture[field], min, max)) add(`evidence.capture.${field}`, 'حد موقعیت عکس معتبر نیست.');
    }
    if (capture.offline_capture !== undefined && typeof capture.offline_capture !== 'boolean') add('evidence.capture.offline_capture', 'گزینهٔ عکس‌برداری بدون اینترنت معتبر نیست.');
    if (capture.challenge_slot != null) {
      const coded = Array.isArray(spec.photos) ? spec.photos.find((photo) => record(photo) && photo.kind === capture.challenge_slot) : null;
      if (!coded) add('evidence.capture.challenge_slot', 'کد دست‌نویس باید روی یکی از عکس‌های همین مرحله باشد.');
      // An optional slot would let the installer skip the code by leaving that photo out.
      else if (!integer(coded.min, 1, 8)) add('evidence.capture.challenge_slot', CHALLENGE_REQUIRED_MESSAGE);
    }
    if (capture.audit_pct !== undefined && !integer(capture.audit_pct, 0, 50)) add('evidence.capture.audit_pct', 'سهم نمونهٔ تصادفی باید عدد صحیحی بین ۰ و ۵۰ باشد.');
  }
  return result(errors);
}

// Process errors carry the stageKey; ord is derived from array order, not accepted from the client.
export function validateStages(stages, version = {}) {
  const errors = [];
  if (version.review_mode !== undefined && !MODES.includes(version.review_mode)) errors.push({ stageKey: null, field: 'review_mode', message: 'نحوهٔ بازبینی معتبر نیست.' });
  if (!Array.isArray(stages) || !integer(stages.length, 1, 20)) return result([...errors, { stageKey: null, field: 'stages', message: 'بین ۱ و ۲۰ مرحله لازم است.' }]);
  const keys = new Set(); const labels = new Set();
  stages.forEach((stage, i) => {
    const stageKey = stage?.key ?? null;
    errors.push(...validateStageDefinition(stage, version).errors.map((e) => ({ stageKey, ...e })));
    const label = typeof stage?.label_fa === 'string' ? stage.label_fa.normalize('NFC').replace(/‌/g, '').trim() : null;
    if (keys.has(stageKey)) errors.push({ stageKey, field: 'key', message: 'شناسهٔ مرحله تکراری است.' });
    if (label && labels.has(label)) errors.push({ stageKey, field: 'label_fa', message: `مرحلهٔ ${String(i + 1).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d])}: دو مرحله نمی‌توانند هم‌نام باشند.` });
    keys.add(stageKey); labels.add(label);
  });
  return result(errors);
}

export const REVIEW_LABELS = { ai_then_supervisor: 'هوش مصنوعی + ناظر', ai_only: 'فقط هوش مصنوعی' };
export const AI_ONLY_WARNING = 'هشدار: تا امروز (۱۳ مهر ۱۴۰۵) هوش مصنوعی در هیچ‌یک از ۱۰۵ بازرسی ثبت‌شده «ادامه» نداده است و با قاعدهٔ قبولی پیش‌فرض فقط ۳ مورد از ۹۲ قبول می‌شد. در حالت «فقط هوش مصنوعی» تقریباً همهٔ مراحل رد می‌شوند تا وقتی قاعدهٔ قبولی یا هوش مصنوعی تنظیم شود. پیش از انتخاب این حالت، سطر «قاعدهٔ قبولی» را در مراحلی که ناظر بررسی کرده است ببینید.';
export const AI_ONLY_HELPERS = {
  cap: 'در حالت «فقط هوش مصنوعی»، اگر هوش مصنوعی پاسخ ندهد تا همین تعداد دوباره تلاش می‌شود و سپس مرحله به صف بررسی می‌رود. هر بار بررسی، چه رد چه بی‌پاسخ، یک بار حساب می‌شود؛ پس از آن هر ارسال به صف بررسی می‌رود.',
  deadline: 'اگر هوش مصنوعی خطا بدهد یا در این مهلت پاسخ ندهد، تا سقف بالا دوباره تلاش می‌شود و سپس مرحله به صف بررسی می‌رود.',
  strict: 'نیروی اجرایی تا نتیجهٔ بررسی منتظر می‌ماند.',
  capture: 'این بررسی‌ها روی سرور انجام می‌شود. در حالت «فقط هوش مصنوعی»، ارسالی که عکس بدون اینترنت دارد، عکسی که از این بررسی‌ها رد شود، تردید در اصالت عکس به صف بررسی می‌رود؛ پس باید کسی این صف را پیگیری کند تا کار نیروی اجرایی معطل نماند. بازبینی نمونه‌ای کیفیت پس از تأیید در صف جداگانه انجام می‌شود و کار را متوقف نمی‌کند.',
};
export const freshPassRule = () => ({ ...DEFAULT_PASS_RULE, block_statuses: [...DEFAULT_PASS_RULE.block_statuses], block_severities: [...DEFAULT_PASS_RULE.block_severities] });
export function newKey(prefix) {
  const bytes = new Uint8Array(3);
  window.crypto.getRandomValues(bytes);
  return prefix + Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}
export const NEW_PHOTO = () => ({ kind: newKey('ph_'), label_fa: 'عکس کلی از کار', min: 1, max: 1, hint_fa: '' });
export const NEW_STAGE = () => ({ key: newKey('st_'), label_fa: '', instructions_fa: '',
  evidence: { fields: [], requires_documents: [], photos: [{ ...NEW_PHOTO(), max: 3 }], note: 'optional', note_min_chars: 0,
    capture: { camera_only: true, gps_required: true, gps_max_accuracy_m: 100, max_distance_m: 150, max_spread_m: null, time_check: true,
      offline_capture: true, challenge_slot: null, audit_pct: 5 } },
  category_id: null, ai_focus_fa: '', gate_mode: 'strict', ai_attempt_cap: 3, ai_deadline_s: 900, review_mode: null, pass_rule: null });
// Database-only metadata and pinned source rulebooks must never ride on a draft save.
export function editableProcess(version) {
  const fields = ['key', 'label_fa', 'instructions_fa', 'evidence', 'category_id', 'ai_focus_fa', 'gate_mode', 'ai_attempt_cap', 'ai_deadline_s', 'review_mode', 'pass_rule', 'warn_minor'];
  return { review_mode: version.review_mode, pass_rule: version.pass_rule,
    stages: (version.stages || []).map((stage) => Object.fromEntries(fields.filter((key) => stage[key] !== undefined).map((key) => [key, stage[key]]))) };
}
export function validateProcess(payload, { publishedCategoryIds, categories = [], brand_name = '' } = {}) {
  const errors = [...validateStages(payload?.stages, payload || {}).errors,
    ...validatePassRule(payload?.pass_rule).errors.map((e) => ({ ...e, stageKey: null }))];
  if (Array.isArray(payload?.stages)) payload.stages.forEach((stage, i) => {
    if (stage?.category_id && publishedCategoryIds && !publishedCategoryIds.includes(stage.category_id)) {
      const category = categories.find((c) => c.id === stage.category_id)?.label_fa || stage.category_id;
      errors.push({ stageKey: stage.key, field: 'category_id', message: `مرحلهٔ ${toFa(i + 1)}: قواعد «${category}» برای ${brand_name} منتشر نشده است.` });
    }
  });
  return result(errors.map((e) => {
    const index = payload?.stages?.findIndex((s) => s?.key === e.stageKey);
    return index >= 0 && !e.message.startsWith('مرحلهٔ') ? { ...e, message: `مرحلهٔ ${toFa(index + 1)} «${payload.stages[index].label_fa || ''}»: ${e.message}` } : e;
  }));
}
export function aiOnlyConsequence(version) {
  const labels = (version?.stages || []).filter((s) => effectiveReview(s, version).mode === 'ai_only').map((s) => `«${s.label_fa}»`);
  return labels.length ? `در این نسخه، مراحل ${labels.join('، ')} بدون ناظر و فقط با قاعدهٔ قبولی تأیید یا رد می‌شوند.` : '';
}

// Work families (IPD S0): code names stay English, labels follow the client document (Q1).
export const WORK_FAMILIES = Object.freeze({ last_mile: 'نصاب لست‌مایل', civil: 'نیروی توسعه' });
export const WORK_FAMILY_KEYS = Object.freeze(Object.keys(WORK_FAMILIES));
export const workFamilyOf = (job) => job?.work_family || 'last_mile';
// A civil job has no customer: its title is the family, its address line the site label.
export const jobTitle = (job) => job?.customer_name || (workFamilyOf(job) === 'civil' ? 'کار توسعه' : 'کار');

export const JOB_KINDS = Object.freeze({
  last_mile: Object.freeze(['new_install', 'repair', 'relocation', 'modem_swap']),
  civil: Object.freeze(['segment', 'manhole', 'fat', 'odc', 'closure', 'restoration']),
});
export const JOB_KIND_LABELS = Object.freeze({ new_install: 'نصب تازه', repair: 'رفع خرابی', relocation: 'جابه‌جایی', modem_swap: 'تعویض مودم',
  segment: 'قطعهٔ مسیر', manhole: 'منهول', fat: 'باکس FAT', odc: 'کابینت ODC', closure: 'مفصل (کلوزر)', restoration: 'مرمت آسفالت و موزاییک' });
export const defaultJobKind = (family) => family === 'civil' ? 'segment' : 'new_install';
export const jobKindOf = (job) => job?.job_kind || defaultJobKind(workFamilyOf(job));
